import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, ChevronDown, ChevronUp, Star, BarChart3, Globe, MessageSquare, Search, Share2, Mail, Smartphone, Bot, Building2, FileText, MapPin, Users, DollarSign, TrendingUp, Zap, Target, MousePointerClick } from 'lucide-react';
import { useState } from 'react';
import INDUSTRIES, { getIndustryBySlug } from '../data/industries';
import { getLocationBySlug, getMarketStats, STATES, METROS } from '../data/locations';
import SEOHead, { createBreadcrumbSchema, createOrganizationSchema } from './SEOHead';

const COLORS = { primary: '#002FA7', accent: '#FFCC00', dark: '#0A1628', light: '#F8FAFC' };
const SITE_URL = 'https://localbrandingpro.com';
const TOOL_ICONS = { 'Listings Management': Globe, 'Review Monitoring': Star, 'Local SEO': Search, 'Social Media': Share2, 'Google Ads': BarChart3, 'SMS Marketing': Smartphone, 'Email Campaigns': Mail, 'AI Visibility': Bot, 'Citation Building': Building2, 'Reputation Management': Star, 'Monthly Reports': FileText, 'Google Business Profile': MapPin };

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-200">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left">
        <span className="text-base font-medium text-slate-800 pr-8" style={{ fontFamily: "'Outfit', sans-serif" }}>{q}</span>
        {open ? <ChevronUp className="h-5 w-5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />}
      </button>
      {open && <p className="pb-5 text-sm text-slate-600 leading-relaxed" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{a}</p>}
    </div>
  );
}

function CityMarketingGuide({ industry, indName, indLower, locName, locShort, loc, market }) {
  const pop = market?.populationFormatted || 'growing';
  const income = market?.incomeFormatted || 'strong';
  const growth = market?.growthRate;
  const growthText = growth > 2 ? 'rapidly growing' : growth > 0 ? 'steadily growing' : 'established';
  const businesses = market?.businessesFormatted || 'numerous';
  const searchVol = market?.searchVolumeFormatted || 'significant';
  const stateAbbr = loc?.abbr || '';
  const region = loc?.type === 'state' ? loc.name : `the ${locShort} area`;

  return (
    <section className="py-16 bg-white" data-testid="city-marketing-guide">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl md:text-3xl font-bold mb-8" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>
          The Complete Guide to {indName} Marketing in {locName}
        </h2>
        <article className="prose prose-slate max-w-none text-sm leading-relaxed space-y-4" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <p>
            If you own or manage a {indLower} business in {locName}, you already know how competitive the local market can be. With a population of {pop} and {businesses} {indLower} businesses competing for attention, standing out in local search results isn't optional — it's essential for survival. The good news? With the right local marketing strategy, your {indLower} business can dominate {locShort}'s digital landscape and consistently attract new customers.
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-8">Why Local SEO Matters for {indName} in {locShort}</h3>
          <p>
            Every month, customers in {locName} perform approximately {searchVol} searches related to {indLower} services. These aren't casual browsers — they're people actively looking for a {indLower} business near them, ready to make a decision. Google's "Local 3-Pack" (the map results that appear at the top of search) captures 44% of all clicks for local searches. If your {indLower} business isn't appearing in those top three positions for "{indLower} in {locShort}" or "{indLower} near me," you're losing customers to competitors who are.
          </p>
          <p>
            Local SEO for {indLower} businesses in {stateAbbr} involves three pillars: Google Business Profile optimization, consistent citation building across 70+ directories, and a steady stream of positive Google reviews. {locShort} is a {growthText} market with median household incomes of {income}, meaning customers have spending power — but they also have high expectations. They compare businesses online before making decisions, and your digital presence is often their first impression.
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-8">Google Business Profile Optimization for {locShort} {indName} Businesses</h3>
          <p>
            Your Google Business Profile (GBP) is the single most important ranking factor for appearing in {locShort}'s local search results. A fully optimized GBP with accurate business information, regular posts, high-quality photos, and active review management signals to Google that your {indLower} business is legitimate, active, and trustworthy. Our platform manages your entire GBP presence: we optimize your business description with {locShort}-specific keywords, schedule weekly Google Business posts, respond to every review, maintain accurate hours across holidays, and run AI-powered GBP audits that score your profile against competitors in {locName}.
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-8">Citation Building: Get Your {indName} Business Listed Everywhere</h3>
          <p>
            NAP consistency (Name, Address, Phone) across the internet is a top-5 ranking factor for local search. When Google sees your {indLower} business listed with identical information on 40, 50, or 70+ directories, it builds enormous trust in your legitimacy. We submit and manage your {locShort} {indLower} business across all major directories: Google, Yelp, Apple Maps, Bing Places, Facebook, Nextdoor, Yellow Pages, BBB, Angi, Thumbtack, and 60+ industry-specific and regional directories relevant to {stateAbbr} businesses. Every citation we build includes your {locName} address, local phone number, and consistent business details — creating a web of signals that tells Google exactly where you are and what you do.
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-8">Review Generation & Reputation Management in {locShort}</h3>
          <p>
            In {locName}'s competitive {indLower} market, reviews are the #1 deciding factor for consumers choosing between businesses. The average business in Google's Local 3-Pack has 47+ reviews. Our automated review request system sends personalized messages to your customers after each visit or service, making it effortless for them to leave a 5-star Google review. For {indLower} businesses in {locShort}, we typically see a 3-5x increase in monthly reviews within the first 60 days. We also monitor all your reviews across Google, Facebook, and Yelp, alerting you to new reviews and providing response templates so you never leave a review unanswered.
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-8">Geo-Fencing: Location-Based Advertising for {locShort} {indName} Businesses</h3>
          <p>
            Geo-fencing is one of the most powerful marketing tools available for {indLower} businesses in {locName}. We create a virtual perimeter around your business location — typically 1-3 miles depending on your market — and target anyone who enters that zone with ads on Facebook and Instagram. Imagine a potential customer walking within a mile of your {indLower} business and seeing an ad that says "{industry.useCases?.[0] || indName} in {locShort} — 0.3 miles away." The proximity makes the ad incredibly relevant, and studies show geo-fenced ads drive 20-30% more foot traffic than traditional digital advertising. We can also set geo-fences around competitor locations in {locShort}, capturing their customers with your targeted messaging.
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-8">Call Tracking: Know Which Marketing Drives Phone Calls</h3>
          <p>
            For {indLower} businesses in {locName}, phone calls are often the most valuable leads. But how do you know which marketing channel drove each call? Our multi-number call tracking assigns unique local {stateAbbr} phone numbers to each of your marketing channels — one for Google Ads, one for your website, one for Facebook, one for direct mail. Every call is forwarded to your real business number seamlessly, while we track the source, record the conversation for quality assurance, and provide detailed analytics. You'll know exactly whether your Google Business Profile, your website, or your Facebook ads are driving the most valuable calls to your {locShort} {indLower} business.
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-8">Social Media Marketing for {indName} in {locName}</h3>
          <p>
            Consistent social media presence builds trust and keeps your {indLower} business top-of-mind with {locShort} customers. Our platform generates AI-powered content tailored specifically to {indLower} businesses and automatically posts to Facebook, Instagram, and Google Business Profile. Content themes include industry tips, behind-the-scenes looks, customer testimonials, seasonal promotions, and community involvement in {locShort}. We also integrate your online ordering, booking links, and review requests directly into social posts for seamless customer conversion.
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-8">Getting Started with {indName} Marketing in {locShort}</h3>
          <p>
            Whether you're a new {indLower} business in {locName} looking to build visibility from scratch, or an established business that wants to outrank growing competition, LocalBranding Pro gives you enterprise-level marketing tools at a fraction of what {locShort} agencies charge. Plans start at $149/month with no long-term contracts — and every plan includes a 7-day free trial so you can see results before committing. With {pop} potential customers in {region} searching for {indLower} services every day, the question isn't whether you can afford local marketing — it's whether you can afford not to.
          </p>
        </article>
      </div>
    </section>
  );
}

export default function LocationLandingPage() {
  const { slug, location } = useParams();
  const industry = getIndustryBySlug(slug);
  const loc = getLocationBySlug(location);

  if (!industry || !loc) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: COLORS.dark }}>
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Page Not Found</h1>
          <Link to="/" className="text-blue-400 hover:underline">Back to Home</Link>
        </div>
      </div>
    );
  }

  const locName = loc.full;
  const locShort = loc.name;
  const indName = industry.name;
  const indLower = indName.toLowerCase();
  const market = getMarketStats(location, slug);

  // Generate location-specific content
  const title = `${indName} Marketing in ${locName} | Local SEO & Listings | LocalBranding Pro`;
  const desc = `Dominate local search for your ${indLower} business in ${locName}. Manage 70+ directory listings, get more Google reviews, run local SEO, and track AI visibility from one dashboard.`;
  const h1 = `${indName} Marketing in ${locName}`;
  const sub = `Help customers in ${locShort} find your ${indLower} business. Dominate local search, get more reviews, and grow with data-driven marketing.`;

  // Location-specific FAQs
  const faqs = [
    { q: `How does LocalBranding Pro help ${indLower} businesses in ${locShort}?`, a: `We optimize your online presence across 70+ directories targeting ${locName} customers, generate Google reviews, run local SEO campaigns for "${indLower} near me" and "${indLower} in ${locShort}" keywords, and manage your social media presence locally.` },
    { q: `How long does it take to rank for "${indLower} in ${locShort}"?`, a: `Most ${indLower} businesses in ${locName} see improved visibility within 30-60 days. Review generation starts immediately, and local SEO improvements typically show within 60-90 days depending on competition.` },
    { q: `Can you help my ${indLower} business compete with larger companies in ${locShort}?`, a: `Absolutely. Strong local reviews and an optimized Google Business Profile help small ${indLower} businesses outrank larger competitors in local search results in ${locName}.` },
    { q: `What local directories matter most for ${indLower} businesses in ${loc.abbr}?`, a: `Google Business Profile is #1 for ${indLower} businesses in ${loc.abbr}. We also optimize Yelp, Bing Places, Apple Maps, Facebook, and 65+ industry-specific and regional directories.` },
    { q: `How much does ${indLower} marketing cost in ${locShort}?`, a: `LocalBranding Pro starts at $149/month for complete local marketing management — listings, reviews, SEO, and social media. That's a fraction of what agencies in ${locName} charge for similar services.` },
  ];

  // Schema stacking
  const schema = [
    createOrganizationSchema(),
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: `${indName} Marketing in ${locName}`,
      provider: { '@type': 'Organization', name: 'LocalBranding Pro', url: SITE_URL },
      description: desc,
      serviceType: 'Digital Marketing',
      areaServed: loc.type === 'state'
        ? { '@type': 'State', name: loc.name, containedInPlace: { '@type': 'Country', name: 'United States' } }
        : { '@type': 'City', name: loc.name, containedInPlace: { '@type': 'State', name: loc.abbr } }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } }))
    },
    createBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: indName, url: `/industries/${slug}` },
      { name: locName }
    ]),
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description: desc,
      url: `${SITE_URL}/industries/${slug}/${location}`,
      isPartOf: { '@type': 'WebSite', name: 'LocalBranding Pro', url: SITE_URL }
    }
  ];

  // Cross-links: other cities for same industry + other industries for same location
  const otherLocations = [...METROS.filter(m => m.slug !== location).slice(0, 8), ...STATES.filter(s => s.slug !== location).slice(0, 4)].slice(0, 10);
  const otherIndustries = INDUSTRIES.filter(i => i.slug !== slug).slice(0, 8);

  // Location-specific use cases
  const useCases = industry.useCases.map(uc => `${uc} in ${locShort}`);

  return (
    <>
      <SEOHead title={title} description={desc} schema={schema} />
      <div className="min-h-screen" style={{ background: COLORS.light }}>
        {/* Nav */}
        <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: COLORS.primary }}><span className="text-white font-bold text-sm">LB</span></div>
              <span className="font-bold text-lg" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>LocalBranding Pro</span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/#features" className="text-sm text-slate-600 hover:text-slate-900">Features</Link>
              <Link to="/#pricing" className="text-sm text-slate-600 hover:text-slate-900">Pricing</Link>
              <Link to="/blog" className="text-sm text-slate-600 hover:text-slate-900">Industry News</Link>
              <Link to="/admin/login" className="text-sm font-medium" style={{ color: COLORS.primary }}>Log In</Link>
              <Link to="/admin/login" className="px-4 py-2 rounded-full text-sm font-semibold text-white" style={{ background: COLORS.primary }}>Start Free Trial</Link>
            </div>
          </div>
        </nav>

        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-6 py-3">
          <nav className="flex text-xs text-slate-500 flex-wrap" data-testid="breadcrumb" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-slate-700">Home</Link><span className="mx-2">/</span>
            <Link to={`/industries/${slug}`} className="hover:text-slate-700">{indName}</Link><span className="mx-2">/</span>
            <span style={{ color: COLORS.primary }} className="font-medium">{locName}</span>
          </nav>
        </div>

        {/* Hero */}
        <section className="py-16 md:py-20" data-testid="location-hero">
          <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] mb-4 inline-block" style={{ color: COLORS.primary }}>
                <MapPin className="inline h-3 w-3 mr-1" />{locName}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>{h1}</h1>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{sub}</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/admin/login" className="px-6 py-3 rounded-full text-sm font-semibold text-white flex items-center gap-2 transition-transform hover:scale-105" style={{ background: COLORS.primary }} data-testid="hero-cta">
                  Start Free Trial <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to={`/industries/${slug}`} className="px-6 py-3 rounded-full text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">
                  All {indName} Features
                </Link>
              </div>
              <div className="flex gap-6 mt-4 text-xs text-slate-500"><span>14-day free trial</span><span>No credit card</span><span>Cancel anytime</span></div>
            </div>
            <div className="relative">
              <img src={industry.image} alt={`${indName} professional in ${locName}`} className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]" loading="eager" />
            </div>
          </div>
        </section>

        {/* Local Market Data */}
        {market && (
          <section className="py-12 border-b border-slate-100" data-testid="market-stats-section">
            <div className="max-w-7xl mx-auto px-6">
              <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3 text-center" style={{ color: COLORS.primary }}>Market Intelligence</p>
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>
                {locShort} {indLower} market at a glance
              </h2>
              <p className="text-center text-slate-500 text-sm mb-10 max-w-xl mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                Hyper-local data to help you understand the competitive landscape
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm text-center" data-testid="stat-population">
                  <Users className="h-5 w-5 mx-auto mb-2" style={{ color: COLORS.primary }} />
                  <p className="text-2xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>{market.populationFormatted}</p>
                  <p className="text-xs text-slate-500 mt-1">Population</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm text-center" data-testid="stat-income">
                  <DollarSign className="h-5 w-5 mx-auto mb-2" style={{ color: '#10b981' }} />
                  <p className="text-2xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>{market.incomeFormatted}</p>
                  <p className="text-xs text-slate-500 mt-1">Median Income</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm text-center" data-testid="stat-growth">
                  <TrendingUp className="h-5 w-5 mx-auto mb-2" style={{ color: market.growthRate > 0 ? '#10b981' : '#ef4444' }} />
                  <p className="text-2xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>{market.growthFormatted}</p>
                  <p className="text-xs text-slate-500 mt-1">Growth Rate</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm text-center" data-testid="stat-businesses">
                  <Building2 className="h-5 w-5 mx-auto mb-2" style={{ color: '#f59e0b' }} />
                  <p className="text-2xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>{market.businessesFormatted}</p>
                  <p className="text-xs text-slate-500 mt-1">{indName} Businesses</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm text-center" data-testid="stat-search-volume">
                  <Search className="h-5 w-5 mx-auto mb-2" style={{ color: '#8b5cf6' }} />
                  <p className="text-2xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>{market.searchVolumeFormatted}</p>
                  <p className="text-xs text-slate-500 mt-1">Monthly Searches</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm text-center" data-testid="stat-cpc">
                  <MousePointerClick className="h-5 w-5 mx-auto mb-2" style={{ color: '#ec4899' }} />
                  <p className="text-2xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>{market.cpcFormatted}</p>
                  <p className="text-xs text-slate-500 mt-1">Avg. CPC</p>
                </div>
              </div>
              <div className="mt-6 text-center">
                <span className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full" style={{
                  background: market.competition === 'Very High' ? '#fef2f2' : market.competition === 'High' ? '#fefce8' : '#f0fdf4',
                  color: market.competition === 'Very High' ? '#dc2626' : market.competition === 'High' ? '#ca8a04' : '#16a34a',
                }}>
                  <Target className="h-4 w-4" />
                  Competition Level: {market.competition}
                </span>
              </div>
            </div>
          </section>
        )}

        {/* Why Location Matters */}
        <section className="py-16" style={{ background: '#f1f5f9' }}>
          <div className="max-w-7xl mx-auto px-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3 text-center" style={{ color: COLORS.primary }}>Local matters</p>
            <h2 className="text-3xl font-bold text-center mb-4" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>
              Why {indLower} businesses in {locShort} need local marketing
            </h2>
            <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              When customers in {locName} search for {indLower} services, your business needs to appear at the top. Here's what LocalBranding Pro does for you.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {industry.solutions.map((s, i) => (
                <div key={i} className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm">
                  <h3 className="font-semibold text-slate-800 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                    {s.desc.replace(/your (service )?area/gi, locName)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16" style={{ background: COLORS.dark }}>
          <div className="max-w-7xl mx-auto px-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3 text-center" style={{ color: COLORS.accent }}>Services we help market</p>
            <h2 className="text-3xl font-bold text-center mb-12 text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {indName} marketing services in {locShort}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {useCases.map((uc, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/5 rounded-lg p-4 border border-white/10">
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0" style={{ color: COLORS.accent }} />
                  <span className="text-sm text-white/90" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{uc}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ UNIQUE LOCAL MARKETING GUIDE — 750+ words ═══ */}
        <CityMarketingGuide industry={industry} indName={indName} indLower={indLower} locName={locName} locShort={locShort} loc={loc} market={market} />

        {/* Tools Grid */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-center mb-12" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>
              All the tools your {locShort} {indLower} business needs
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {industry.tools.map((tool, i) => {
                const Icon = TOOL_ICONS[tool] || Globe;
                return (
                  <div key={i} className="flex items-center gap-3 bg-white rounded-lg p-4 border border-slate-100 shadow-sm">
                    <Icon className="h-5 w-5" style={{ color: COLORS.primary }} />
                    <span className="text-sm font-medium text-slate-700" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{tool}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16" style={{ background: '#f1f5f9' }}>
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-center mb-12" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>
              {indName} marketing FAQ for {locName}
            </h2>
            <div className="bg-white rounded-xl shadow-sm divide-y divide-slate-100 px-6">
              {faqs.map((faq, i) => <FAQItem key={i} q={faq.q} a={faq.a} />)}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>
              Ready to grow your {indLower} business in {locShort}?
            </h2>
            <p className="text-slate-600 mb-8" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              Join {indLower} businesses in {locName} that use LocalBranding Pro to dominate local search.
            </p>
            <Link to="/admin/login" className="inline-flex items-center gap-2 px-8 py-3 rounded-full text-sm font-semibold text-white transition-transform hover:scale-105" style={{ background: COLORS.primary }} data-testid="bottom-cta">
              Start Free Trial <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

        {/* Cross-links: Other cities */}
        <section className="py-8 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-6">
            <h3 className="text-sm font-semibold text-slate-500 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{indName} marketing in other locations:</h3>
            <div className="flex flex-wrap gap-2 mb-6">
              {otherLocations.map(l => (
                <Link key={l.slug} to={`/industries/${slug}/${l.slug}`} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-700 transition-colors">
                  {l.full || l.name}
                </Link>
              ))}
            </div>
            <h3 className="text-sm font-semibold text-slate-500 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Other industries in {locShort}:</h3>
            <div className="flex flex-wrap gap-2">
              {otherIndustries.map(ind => (
                <Link key={ind.slug} to={`/industries/${ind.slug}/${location}`} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-700 transition-colors">
                  {ind.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-500">&copy; {new Date().getFullYear()} LocalBranding Pro. All rights reserved.</p>
            <p className="text-xs text-slate-500">A <a href="https://web-techservices.com" className="text-slate-600 hover:text-slate-800 transition-colors">Web-TechServices AI</a> product.</p>
            <div className="flex gap-4 text-xs text-slate-500">
              <Link to="/" className="hover:text-slate-700">Home</Link>
              <Link to="/blog" className="hover:text-slate-700">Industry News</Link>
              <Link to="/admin/login" className="hover:text-slate-700">Admin Login</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
